import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";
import EventLoginForm from "./EventLoginForm";
import SpinningWheel from "./SpinningWheel";
import { useTranslation } from "react-i18next";
import { supabase } from "../supabaseClient";
import AllDealsIcon from "../assets/all-deals-icon.svg";
import InviteIcon from "../assets/invite-icon.png";
import Modal from "./Modal";

const SpinningWheelSection = ({ event, redirectUrl }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);

  const [hasSpun, setHasSpun] = useState(false);
  const [spinInProgress, setSpinInProgress] = useState(false);

  const [shareUrl, setShareUrl] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [previousResult, setPreviousResult] = useState(null);
  const [plusOneAvailable, setPlusOneAvailable] = useState(false);

  const handleShareTicket = async () => {
    if (!previousResult?.prize_id || !event?.id) return;

    try {
      const { data, error } = await supabase.functions.invoke(
        "claimPlusOneCode",
        {
          body: JSON.stringify({
            userId: user.id,
            eventId: event.id,
            prizeId: previousResult.prize_id,
          }),
        }
      );

      if (error) {
        console.error("Error claiming +1 code:", error);
        return;
      }

      // Use Web Share API if available
      if (navigator.share) {
        try {
          await navigator.share({
            title: `${event.name} - Extra Ticket`,
            text: `I've got an extra ticket for ${event.name}. Join me by using this link:`,
            url: data.shareUrl,
          });
          console.log("Content shared successfully!");
        } catch (shareError) {
          console.error("Error sharing content:", shareError);
          // Fallback to modal if sharing was cancelled or failed
          setShareUrl(data.shareUrl);
          setShowShareModal(true);
        }
      } else {
        // Fallback for browsers that don't support the Web Share API
        setShareUrl(data.shareUrl);
        setShowShareModal(true);
      }
    } catch (err) {
      console.error("Failed to claim +1 code:", err);
    }
  };

  if (!user.id) {
    return <EventLoginForm redirectUrl={redirectUrl} />;
  }

  return (
    <>
      <div className="z-[10000] flex flex-col items-center mx-6 mt-12 lg:mt-20 px-[16px] py-[24px] lg:w-[450px] max-w-[95vw] social-proof-card">
        <div className="flex justify-between items-center mb-[30px] w-full">
          <div className="font-semibold text-[26px] text-white text-start">
            {t("spin_win_tickets", "Spin & win tickets")}
          </div>
          <div className="bg-[#918D99] ml-2 px-[8px] py-[2px] rounded-[4px] font-bold text-[16px] text-clip uppercase">
            Beta
          </div>
        </div>
        <SpinningWheel
          hasSpun={hasSpun}
          setHasSpun={setHasSpun}
          event={event}
          spinInProgress={spinInProgress}
          setSpinInProgress={setSpinInProgress}
          plusOneAvailable={plusOneAvailable}
          setPlusOneAvailable={setPlusOneAvailable}
          previousResult={previousResult}
          setPreviousResult={setPreviousResult}
        />

        {!hasSpun && !spinInProgress ? (
          <div className="mt-[30px]">
            <div className="flex justify-between items-center">
              <div className="font-semibold text-[22px] text-white">
                Hoe werkt het?
              </div>
            </div>
            <div className="gap-4 grid grid-cols-2 mt-[12px]">
              <div className="bg-[#1D1726] p-4 border border-[#AD7AEF40] rounded-lg">
                <h3 className="mb-2 font-bold text-[14px] text-white">
                  {t("spin_the_wheel", "Spin the wheel")}
                </h3>
                <p className="text-[13px] text-neutral-300">
                  {t(
                    "spin_wheel_instruction",
                    "Druk op de knop en maak direct of je een prijs wint."
                  )}
                </p>
              </div>
              <div className="bg-[#1D1726] p-4 border border-[#AD7AEF40] rounded-lg">
                <h3 className="mb-2 font-bold text-[14px] text-white">
                  {t("win_prizes", "Win te gekke prijzen")}
                </h3>
                <p className="text-[13px] text-neutral-300">
                  {t(
                    "win_prizes_description",
                    "Maak kans op tickets, speciale acties, meet & greets en vele verrassingen."
                  )}
                </p>
              </div>
            </div>
          </div>
        ) : (
          spinInProgress && (
            <div className="mt-[20px] w-full text-left">
              <div className="font-semibold text-[22px] text-white text-left">
                Aan het spinnen...
              </div>
              <div className="mt-2 text-[16px] text-neutral-200">
                Okaaayyyy, let's go!
              </div>
            </div>
          )
        )}
      </div>

      {hasSpun && previousResult?.prize_id && plusOneAvailable && event.plus_one_invites_enabled !== false && (
        <div className="z-[10000] flex flex-col items-center mx-6 mt-12 px-[16px] py-[24px] lg:w-[450px] max-w-[95vw] text-start social-proof-card">
          <h3 className="w-full font-semibold text-[22px] text-white text-start">
            {t("invite_plus_one", "Nodig je +1 uit!")}
          </h3>
          <div className="bg-[#1D1726] mt-[6px] mb-[12px] p-[20px_16px] border border-[#AD7AEF50] rounded-[8px] text-left">
            <div className="flex items-start gap-[12px] mb-2">
              <img
                src={AllDealsIcon}
                className="w-[24px] h-[24px]"
                alt="Ticket"
              />
              <div className="flex flex-col">
                <span className="font-[700] text-[16px] text-white">
                  {t("extra_ticket", "Extra ticket")}
                </span>
                <p className="mt-1 text-[14px] text-gray-300">
                  {previousResult.prize?.title || "Frenzy x Vulged"}
                </p>
              </div>
            </div>

            <hr className="my-[12px] border-[#292433]" />

            <div className="flex items-start gap-[12px] mb-2">
              <img src={InviteIcon} className="w-[24px] h-[24px]" alt="Email" />
              <div className="flex flex-col">
                <span className="font-[700] text-[16px] text-white">
                  {t("bring_someone", "Iemand meenemen?")}
                </span>
                <p className="mt-1 text-[14px] text-gray-300">
                  {t("share_second_ticket", "Deel je 2e ticket met een vriend of vriendin en ga samen op pad.")}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={handleShareTicket}
            className="mt-4 !py-[12px] rounded-[8px] w-full font-semibold !text-[17px] text-white premium-button"
          >
            {t("share_ticket", "Deel ticket")}
          </button>
        </div>
      )}

      <Modal
        show={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={t("share_ticket_with_friend", "Deel je ticket met een vriend")}
      >
        <p className="mb-4 text-white">
          {t("share_link_description", "Stuur deze link naar je vriend. Zodra ze zich aanmelden, krijgen ze automatisch een ticket!")}
        </p>

        <div className="flex justify-between items-center bg-white mt-4 py-2 pr-2 max-lg:!pb-2 pl-4 border border-neutral-400 rounded-[4px] w-full">
          <p className="font-medium text-[14px] text-purple-600 truncate">
            {shareUrl}
          </p>
          <button
            className="disabled:bg-[#56466B] enabled:bg-gradient-to-r enabled:from-[#6A1BD1] enabled:to-[#7721E5] shadow-md px-[10px] py-[8px] rounded-[4px] font-semibold text-[16px] text-white hover:text-white !disabled:cursor-not-allowed btn"
            onClick={() => {
              navigator.clipboard.writeText(shareUrl);
              // setShowShareModal(false);
            }}
          >
            {t("copy", "Copy")}
          </button>
        </div>
        <p className="mt-4 text-white">
          {t("manual_link_instruction", "If you can't copy the link, you can use this link instead:")}{" "}
          {shareUrl}
        </p>
      </Modal>
    </>
  );
};

export default SpinningWheelSection;
